import React, {useState} from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {
  getDefaultDataFrameColumnMapping,
  isDataFrameColumnMappingComplete,
} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import AnalysisViewSelector from 'analysis-tool/ui/components/AnalysisViewSelector';
import {assert} from 'Asserts';
import getUserColumns from 'data-frames/util/getUserColumns';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getOutputDataForLineageNode, getOutputDataForView} from 'pipelines/PipelineViewUtils';
import Modal from 'ui-library/modals/Modal';

import {getInputTableApplyStateName, InputTableApplyState} from './AnalysisTemplateUtil';
import AnalysisTemplateInputTableColumnMapper from './table-mapping/AnalysisTemplateInputTableColumnMapper';

export interface AnalysisTemplateTableSelectorModalProps {
  analysis: AnalysisGraphQL;
  inputTable: InputTableApplyState;
  onRequestSubmit: (result: {
    table: PipelineViewGraphQL;
    columnMapping: {[templateColumn: string]: string};
  }) => void;
  onRequestClose: () => void;
  getInputTableDisabledMessage?: (
    analysis: AnalysisGraphQL,
    inputTable: PipelineViewGraphQL,
    selectedTable: PipelineViewGraphQL | null
  ) => string | null;
}

export default function AnalysisTemplateTableSelectorModal({
  analysis,
  inputTable,
  onRequestSubmit,
  onRequestClose,
  getInputTableDisabledMessage,
}: AnalysisTemplateTableSelectorModalProps): JSX.Element {
  const name = getInputTableApplyStateName(inputTable);

  const [selectedView, setSelectedView] = useState<PipelineViewGraphQL | null>(inputTable.table);

  const templateDataFrame =
    inputTable.templateLineageNode != null
      ? getOutputDataForLineageNode(inputTable.templateLineageNode, undefined, PipelineStepDataKind.DATASET)
      : inputTable.dataFrameColumns
        ? {
            __typename: 'DataFrameNode' as const,
            columns: inputTable.dataFrameColumns,
            name,
          }
        : null;
  assert(templateDataFrame != null, 'Expected either templateLineageNode or dataFrameColumns to be defined');

  const templateDataFrameColumns = getUserColumns({
    columns: templateDataFrame.columns,
  });

  const selectedDataFrame =
    selectedView != null ? getOutputDataForView(selectedView, undefined, PipelineStepDataKind.DATASET) : null;

  const initialColumnMapping =
    inputTable.columnMapping ??
    getDefaultDataFrameColumnMapping(templateDataFrameColumns, selectedDataFrame?.columns ?? []);

  const [columnMapping, setColumnMapping] = useState<Record<string, string>>(initialColumnMapping);

  const analysisTableSelector = (
    <AnalysisViewSelector
      analysis={analysis}
      selectedView={selectedView}
      onSelect={(view) => {
        setSelectedView(view);
        const defaultColumnMapping = getDefaultDataFrameColumnMapping(
          templateDataFrameColumns,
          view != null ? getOutputDataForView(view, undefined, PipelineStepDataKind.DATASET).columns : []
        );
        setColumnMapping(defaultColumnMapping);
      }}
      topLevelOnly={true}
      dataKind={PipelineStepDataKind.DATASET}
      getDisabledMessage={(view) => getInputTableDisabledMessage?.(analysis, view, inputTable.table) ?? null}
      dataTestAttrs={{
        component: 'AnalysisTemplateTableSelectorModal',
        element: 'analysisTableSelector',
      }}
    />
  );

  return (
    <Modal
      fullWidth={true}
      bodyPadded={false}
      expandHeight={true}
      title={`Select table for ${name}`}
      onRequestSubmit={() => {
        assert(selectedView != null, 'Expected view to be selected for submit to be enabled');
        onRequestSubmit({
          table: selectedView,
          columnMapping,
        });
      }}
      onRequestClose={onRequestClose}
      buttons={[
        {
          cancel: true,
          text: 'Cancel',
        },
        {
          submit: true,
          text: 'Save',
          disabled:
            selectedView == null ||
            !isDataFrameColumnMappingComplete(templateDataFrameColumns, columnMapping),
        },
      ]}
    >
      <AnalysisTemplateInputTableColumnMapper
        columnMapping={columnMapping}
        setColumnMapping={setColumnMapping}
        inputTableColumns={selectedDataFrame?.columns ?? null}
        inputTableDataFrameIdForPreview={selectedDataFrame?.id ?? null}
        inputTableSelector={analysisTableSelector}
        inputTableSelectorLabel="Analysis table"
        templateTableDataFrame={templateDataFrame}
        templateTableLabel="Starting table"
        templateTableDescription={inputTable.description}
        instructions="Select an existing table to use in place of this variable starting table."
      />
    </Modal>
  );
}
